import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Trans, useTranslation } from '@clofo/i18n'
import { clofoApi } from '@clofo/data/api'
import { Button } from '@clofo/ui/button'
import { FormInput } from '@clofo/ui/controlled-field'
import { SubmitButton } from '@clofo/ui/submit-button'
import { usePopup } from '@clofo/ui/hooks'
import { logger } from '@clofo/lib/logger'

import { DMS_LOGIN_STORAGE_KEY, parseDmsLoginRedirect } from '../lib/dms-login'
import { getRedirectTo } from '../lib/redirect'
import { SAML_SESSION_STORAGE_KEY, parseSamlSessionData } from '../lib/saml-session'

type RequestEmailFormProps = {
  /** Called when the email's domain is not SSO-enabled and a password is required next. */
  onPasswordRequired: (email: string) => void
}

/**
 * First step of the adaptive login flow. The user enters their email and `/api/v1/auth/login_method`
 * says how they sign in: `sso` and `dms` (Login with Work) open the provider in a popup and
 * redirect this window to the `next` destination once the popup completes the flow; `password`
 * advances to the password step. Mirrors the legacy login app's `request-email` state.
 */
export const RequestEmailForm = ({ onPasswordRequired }: RequestEmailFormProps) => {
  const { t } = useTranslation()
  const popup = usePopup()
  const [waitingFor, setWaitingFor] = useState<'sso' | 'dms' | null>(null)

  const schema = z.object({ email: z.email({ error: t('errors.validEmail') }) })
  type Values = z.infer<typeof schema>

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  })

  /**
   * Opens the provider in a popup and waits for it to post the outcome. The popup's last page is
   * backend-rendered and writes to `storageKey` — same-origin writes fire a `storage` event here
   * in the opener (the mechanism the legacy Ember login app uses). Resolves only when the flow
   * fails; on success this window navigates away, so the form stays submitting until then.
   */
  const loginWithPopup = ({
    method,
    url,
    storageKey,
    completeLogin,
  }: {
    method: 'sso' | 'dms'
    url: string
    storageKey: string
    completeLogin: (raw: string) => Promise<boolean>
  }) =>
    new Promise<void>(resolve => {
      setWaitingFor(method)
      const fail = (message: string) => {
        window.removeEventListener('storage', onStorage)
        setWaitingFor(null)
        form.setError('email', { message })
        resolve()
      }
      const onStorage = async (event: StorageEvent) => {
        if (event.key !== storageKey || !event.newValue) return
        window.removeEventListener('storage', onStorage)
        popup.close()
        if (await completeLogin(event.newValue)) {
          window.location.href = getRedirectTo()
          return
        }
        fail(t('login.errors.generic'))
      }
      // Clear any leftover payload first — writing a value identical to the stored one would not
      // fire a storage event.
      window.localStorage.setItem(storageKey, '')
      window.addEventListener('storage', onStorage)
      popup.open({
        url,
        name: `clofo-${method}`,
        onError: errorType =>
          fail(
            errorType === 'blocked'
              ? t('login.errors.popupBlocked')
              : t('login.errors.popupClosedEarly'),
          ),
      })
    })

  const completeDmsLogin = async (email: string, raw: string) => {
    // The value carries a live authorization code, so don't leave it lying around.
    window.localStorage.removeItem(DMS_LOGIN_STORAGE_KEY)
    const redirect = parseDmsLoginRedirect(raw)
    if (!redirect) return false
    try {
      await clofoApi.auth.exchangeDmsLoginCode({ email, ...redirect })
      return true
    } catch (err) {
      logger.error(err)
      return false
    }
  }

  const onSubmit = async ({ email }: Values) => {
    try {
      const { method, url } = await clofoApi.auth.loginMethod({ email })
      if (method === 'sso') {
        // Carry the login URL's `next` onto the IdP URL so SSO returns the popup to the user's
        // destination. `next` is never sent to login_method itself.
        const next = window.location.href.match(/next=([^&]*)/)?.[1]
        await loginWithPopup({
          method,
          url: next ? `${url}${url.includes('?') ? '&' : '?'}next=${next}` : url,
          storageKey: SAML_SESSION_STORAGE_KEY,
          completeLogin: async raw => parseSamlSessionData(raw).ok,
        })
        return
      }
      if (method === 'dms') {
        await loginWithPopup({
          method,
          url,
          storageKey: DMS_LOGIN_STORAGE_KEY,
          completeLogin: raw => completeDmsLogin(email, raw),
        })
        return
      }
      onPasswordRequired(email)
    } catch {
      form.setError('email', { message: t('login.errors.generic') })
    }
  }

  const waitingStatus =
    waitingFor &&
    { sso: t('login.waitingForLogin'), dms: t('login.waitingForWorkLogin') }[waitingFor]

  return (
    <div className='flex flex-col max-w-sm w-full'>
      {waitingFor ? (
        <div className='flex flex-col text-center'>
          <p className='text-secondary-foreground text-xs mb-1'>{t('login.siginAs')}</p>
          <p className='mb-8 font-semibold text-lg'>{form.getValues('email')}</p>
          <p>
            <Trans
              t={t}
              i18nKey='login.popup'
              components={{
                refocus: (
                  <Button
                    type='button'
                    variant='link'
                    onClick={popup.focus}
                    className='inline h-auto p-0 text-sm font-normal underline align-baseline'
                  />
                ),
              }}
            />
          </p>
        </div>
      ) : (
        <form onSubmit={form.handleSubmit(onSubmit)} className='flex flex-col'>
          <p className='text-center mb-3'>{t('login.emailLabel')}</p>
          <FormInput
            control={form.control}
            name='email'
            type='email'
            label={t('login.emailPlaceholder')}
            hideLabel
            placeholder={t('login.emailPlaceholder')}
            autoComplete='email'
            autoFocus
            className={{ input: 'h-15' }}
          />
          <SubmitButton
            type='submit'
            size='lg'
            className='mt-6'
            isSubmitting={form.formState.isSubmitting}
          >
            {t('login.next')}
          </SubmitButton>
        </form>
      )}
      {/* Always-mounted status region so the waiting announcement is picked up reliably. */}
      <p role='status' className='sr-only'>
        {waitingStatus}
      </p>
    </div>
  )
}
